import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Card, Input, Modal, Skeleton, useToast } from '../../design-system';
import '../styles/ClientRequests.css';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : 'Never';

// Portal logins for one client, on the client record.
//
// Creating one grants an outsider access to this client's data, so the UI is
// deliberately blunt about it: the temporary password is shown exactly once and
// the copy says so, because there is no way to retrieve it later — only reset.
function ClientPortalUsers({ clientId, contacts = [] }) {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', contactId: '' });
  const [saving, setSaving] = useState(false);
  // The one-time password reveal, for a create or a reset.
  const [reveal, setReveal] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/api/clients/${clientId}/portal-users`);
      setUsers(res.data);
    } catch (err) {
      // A non-manager simply can't see this list; that's not an error worth a toast.
      if (err.response?.status !== 403) {
        toast.danger(err.response?.data?.error || 'Failed to load portal logins');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // Picking an existing contact prefills from the client's own contact list,
  // so the same person isn't retyped (and misspelled) here.
  const pickContact = (contactId) => {
    const c = contacts.find((x) => (x._id || x.id) === contactId);
    setForm((prev) => ({
      ...prev,
      contactId,
      name: c?.name || prev.name,
      email: c?.email || prev.email,
      phone: c?.phone || prev.phone,
    }));
  };

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.danger('Name and email are required');
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.post(`/api/clients/${clientId}/portal-users`, form);
      setUsers((prev) => [res.data, ...prev]);
      setAddOpen(false);
      setForm({ name: '', email: '', phone: '', contactId: '' });
      setReveal({ email: res.data.email, password: res.data.temporaryPassword, isNew: true });
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to create the login');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (user) => {
    const next = !user.isActive;
    if (!next && !window.confirm(`Disable portal access for ${user.email}? They'll be signed out immediately.`)) {
      return;
    }
    try {
      const res = await apiClient.patch(`/api/clients/${clientId}/portal-users/${user.id}`, { isActive: next });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? res.data : u)));
      toast.success(next ? 'Access enabled' : 'Access disabled');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update');
    }
  };

  const resetPassword = async (user) => {
    if (!window.confirm(`Reset the password for ${user.email}? They'll be signed out everywhere.`)) return;
    try {
      const res = await apiClient.post(`/api/clients/${clientId}/portal-users/${user.id}/reset-password`);
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...res.data } : u)));
      setReveal({ email: user.email, password: res.data.temporaryPassword, isNew: false });
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to reset');
    }
  };

  const remove = async (user) => {
    if (!window.confirm(`Delete the portal login for ${user.email}? Their past messages are kept.`)) return;
    try {
      await apiClient.delete(`/api/clients/${clientId}/portal-users/${user.id}`);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      toast.success('Login removed');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove');
    }
  };

  return (
    <Card padding="md">
      <div className="cr-card-head">
        <div>
          <span className="cr-card-title">Portal logins</span>
          <p className="cr-muted">
            People at this client who can sign in to see requests, send documents and message you.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>+ Add login</Button>
      </div>

      {loading ? (
        <Skeleton.Text lines={3} />
      ) : users.length === 0 ? (
        <p className="cr-muted">
          No portal access yet. Adding a login lets this client upload documents themselves
          instead of emailing them.
        </p>
      ) : (
        <ul className="cr-files">
          {users.map((u) => (
            <li key={u.id} className="cr-file-row">
              <span className="cr-file-name">
                <span>
                  <strong>{u.name}</strong> · {u.email}
                  {!u.isActive && <span className="cr-internal-tag">Disabled</span>}
                  {u.mustChangePassword && u.isActive && (
                    <span className="cr-badge-client">Password not set</span>
                  )}
                </span>
              </span>
              <span className="cr-portal-user-actions">
                <span className="cr-muted">Last in: {fmtDate(u.lastLoginAt)}</span>
                <button type="button" onClick={() => resetPassword(u)}>Reset password</button>
                <button type="button" onClick={() => toggleActive(u)}>
                  {u.isActive ? 'Disable' : 'Enable'}
                </button>
                <button type="button" className="cr-danger" onClick={() => remove(u)}>Delete</button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add a portal login"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={create} loading={saving}>Create login</Button>
          </>
        }
      >
        {contacts.length > 0 && (
          <div className="ds-field">
            <label className="ds-field-label" htmlFor="cpu-contact">Use an existing contact</label>
            <select
              id="cpu-contact"
              className="ds-input"
              value={form.contactId}
              onChange={(e) => pickContact(e.target.value)}
            >
              <option value="">— enter details manually —</option>
              {contacts.map((c) => (
                <option key={c._id || c.id} value={c._id || c.id}>
                  {c.name}{c.email ? ` (${c.email})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          helperText="This is the username they sign in with."
        />
        <Input label="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </Modal>

      {/* One-time password reveal. There is no second chance to read this, so
          it's a blocking modal with the value selectable, not a toast. */}
      <Modal
        isOpen={!!reveal}
        onClose={() => setReveal(null)}
        title={reveal?.isNew ? 'Login created' : 'Password reset'}
        size="sm"
        footer={<Button variant="primary" onClick={() => setReveal(null)}>Done</Button>}
      >
        <p className="cr-muted">Send these to <strong>{reveal?.email}</strong>. They&apos;ll be asked to choose their own password on first sign-in.</p>
        <div className="cr-password-reveal">
          <code>{reveal?.password}</code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(reveal.password);
              toast.success('Copied');
            }}
          >
            Copy
          </button>
        </div>
        <p className="cr-warn">
          ⚠️ This password is shown once and cannot be retrieved later — only reset.
        </p>
      </Modal>
    </Card>
  );
}

export default ClientPortalUsers;
